import { alpha, Box, styled } from '@chronosphereio/chrono-ui';
// eslint-disable-next-line no-restricted-imports
import { Tooltip, TooltipProps, tooltipClasses } from '@mui/material';
import { MouseEventHandler, useMemo, useRef, useState } from 'react';
import { HeatMapYAxisOptions, MIN_STEP_FALLBACK_MS } from '@/core';
import { HeatMapChartProps } from './HeatMapChart';
import { HeatMapTooltipContent } from './HeatMapTooltipContent';
import { DEFAULT_UNIT, heatmapInfoAtPosition, Position, xPositionToTimestamp } from './heat-map-model';

// MUI tooltip (since we need 'followCursor'), styled to match other viz-framework tooltips
const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.popover,
    borderRadius: '4px',
    boxShadow: theme.shadows[5],
  },
}));

export type HeatMapInteractivePaneProps = Pick<HeatMapChartProps, 'width' | 'height' | 'data' | 'onDataZoom'> & {
  unit: HeatMapYAxisOptions['unit'];
  onTimestampHover?: (timestampMs: number | undefined) => void;
};

/**
 * Transparent pane floating over the heatmap canvas, handling mouse interactivity
 * - render box around the hovered cell
 * - show a tooltip with info for the hovered cell
 */
export function HeatMapInteractivePane({
  width,
  height,
  data,
  onDataZoom,
  unit = DEFAULT_UNIT,
  onTimestampHover,
}: HeatMapInteractivePaneProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const cellRef = useRef<HTMLDivElement>(null);
  const [inside, setInside] = useState<boolean>();
  const [mousePosition, setMousePosition] = useState<Position>();
  const [dragging, setDragging] = useState<boolean>();
  const [dragX, setDragX] = useState<number>();

  const stepMs = data.timeRange.stepMs ?? MIN_STEP_FALLBACK_MS;

  const handleDown: MouseEventHandler<HTMLDivElement> = (e) => {
    if (containerRef.current) {
      setDragging(true);
      setDragX(e.clientX - containerRef.current.getBoundingClientRect().left);
    }
  };

  const handleUp: MouseEventHandler<HTMLDivElement> = () => {
    setDragging(false);
    if (dragging && dragX && mousePosition) {
      const startX = Math.min(dragX, mousePosition.x);
      const endX = Math.max(dragX, mousePosition.x);
      const start = xPositionToTimestamp(startX, width, data.timeRange);
      const end = xPositionToTimestamp(endX, width, data.timeRange);
      if (end - start >= stepMs) {
        onDataZoom({
          start: new Date(start),
          end: new Date(end),
        });
      }
    }
  };

  const handleLeave: MouseEventHandler<HTMLDivElement> = () => {
    setInside(false);
    setDragging(false);
    onTimestampHover?.(undefined);
  };

  const handleMove: MouseEventHandler<HTMLDivElement> = (e) => {
    // data may change while the cursor is inside the div, resetting inside state to false
    // this ensures the inside state is true if mouse moves inside the div, even if it does not reenter
    if (e.target === containerRef.current || e.target === cellRef.current) setInside(true);

    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      setMousePosition({ x, y });
      onTimestampHover?.(xPositionToTimestamp(x, width, data.timeRange));
    }
  };

  const heatmapHoverInfo = useMemo(() => {
    return mousePosition ? heatmapInfoAtPosition(width, height, mousePosition, data) : null;
  }, [width, height, mousePosition, data]);

  // only show the cell outline if it's not too small
  const showCell = heatmapHoverInfo && heatmapHoverInfo.cell.width > 5 && heatmapHoverInfo.cell.height > 5;

  // We convert all of these pixel values in numbers to explicit string pixel values because for
  // some reason in some edge cases (I think when it's a decimal less than one) MUI would convert
  // them to percentages, which would break the UI in some weird ways by laying things out incorrectly.
  // See sc-105527 for more details.
  const hoveredCellPositionStyles = heatmapHoverInfo?.cell
    ? {
        left: `${heatmapHoverInfo.cell.left}px`,
        top: `${heatmapHoverInfo.cell.top}px`,
        width: `${heatmapHoverInfo.cell.width}px`,
        height: `${heatmapHoverInfo.cell.height}px`,
      }
    : {};

  return (
    <StyledTooltip
      title={heatmapHoverInfo ? <HeatMapTooltipContent info={heatmapHoverInfo} unit={unit} /> : ''}
      followCursor
      placement="right"
    >
      <Box
        ref={containerRef}
        height="100%"
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
        onMouseDown={handleDown}
        onMouseUp={handleUp}
        sx={{ cursor: 'crosshair' }}
      >
        {/* rectangle around the hovered cell */}
        <Box
          ref={cellRef}
          position="absolute"
          visibility={inside && showCell ? 'visible' : 'hidden'}
          {...hoveredCellPositionStyles}
          sx={(theme) => ({ border: '2px solid ' + theme.palette.common.black })}
        />
        {/* dragging area indicator */}
        <Box
          position="absolute"
          top={0}
          height={height}
          visibility={dragging ? 'visible' : 'hidden'}
          left={dragX && mousePosition ? Math.min(dragX, mousePosition.x) : 0}
          width={dragX && mousePosition ? Math.max(dragX, mousePosition.x) - Math.min(dragX, mousePosition.x) : 0}
          sx={(theme) => ({ backgroundColor: alpha(theme.palette.common.white, 0.2) })}
        />
      </Box>
    </StyledTooltip>
  );
}
